import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IntegrationPage, toMessage } from "../components/integration-shell";
import { ApiUnavailableState, LoadingState } from "../components/states";
import {
  AddIntegrationForm,
  IntegrationOverview,
  NO_SECRETS_NOTE,
  type IntegrationAction,
} from "../components/integrations";
import {
  api,
  INTEGRATION_TYPE_NAMES,
  type IntegrationTypeName,
  type SafeIntegration,
  type SafeTestResult,
} from "../lib/api";

/** Where "Configure" routes each integration type. */
function configureHref(type: IntegrationTypeName, id: string): string {
  switch (type) {
    case "RSS":
      return `/app/integrations/rss?id=${id}`;
    case "TWITCH":
    case "YOUTUBE":
      return `/app/integrations/streaming?id=${id}`;
    case "GAME_SERVER":
    case "STEAM_WORKSHOP":
      return `/app/integrations/game?id=${id}`;
    case "N8N":
      return `/app/integrations/n8n?id=${id}`;
    default:
      return `/app/integrations/rss?id=${id}`;
  }
}

export function Integrations(): JSX.Element {
  return (
    <IntegrationPage
      title="Integrations"
      intro={
        <p>
          Every integration is monitored and honest: statuses, health and last-run times all come from the control API. {NO_SECRETS_NOTE}
        </p>
      }
    >
      {(tenantId) => <IntegrationsPanel tenantId={tenantId} />}
    </IntegrationPage>
  );
}

function IntegrationsPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState<SafeIntegration[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [testResults, setTestResults] = useState<Record<string, SafeTestResult | null>>({});

  const load = () => {
    api.integrations
      .list(tenantId)
      .then((r) => {
        setIntegrations(r.integrations);
        setError(null);
      })
      .catch((e) => setError(toMessage(e)));
  };

  useEffect(() => {
    load();
    // load reads the same tenantId it depends on
  }, [tenantId]);

  const create = async (input: { type: IntegrationTypeName; name: string; guildId: string | null }) => {
    setCreateBusy(true);
    setMessage(null);
    try {
      const r = await api.integrations.create(tenantId, input);
      setMessage(`Draft integration "${r.integration.name}" created (${r.integration.status}). It is not active — configure and validate it next.`);
      load();
    } catch (e) {
      setMessage(`Not created: ${toMessage(e)}`);
    } finally {
      setCreateBusy(false);
    }
  };

  const onAction = async (action: IntegrationAction, integration: SafeIntegration) => {
    if (action === "configure") {
      navigate(configureHref(integration.type, integration.id));
      return;
    }
    if (action === "history") {
      navigate(`/app/integrations/workflows?type=`);
      return;
    }
    setBusyId(integration.id);
    setMessage(null);
    try {
      if (action === "validate") {
        const r = await api.integrations.validate(tenantId, integration.id);
        setMessage(r.validation.valid ? `"${integration.name}" configuration is valid.` : `"${integration.name}" has ${r.validation.errors.length} validation error(s).`);
      } else if (action === "test") {
        const r = await api.integrations.test(tenantId, integration.id);
        setTestResults((prev) => ({ ...prev, [integration.id]: r }));
        setMessage(r.note);
      } else if (action === "pause") {
        await api.integrations.pause(tenantId, integration.id);
      } else if (action === "resume") {
        await api.integrations.resume(tenantId, integration.id);
      } else if (action === "revoke") {
        await api.integrations.revoke(tenantId, integration.id);
      }
      load();
    } catch (e) {
      setMessage(toMessage(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="ob-builder">
      <AddIntegrationForm types={INTEGRATION_TYPE_NAMES} onCreate={create} busy={createBusy} />
      {message ? (
        <p className="muted" role="status" style={{ marginBottom: "0.5rem" }}>
          {message}
        </p>
      ) : null}
      {error ? (
        <ApiUnavailableState detail={error} />
      ) : integrations === null ? (
        <LoadingState label="Reading integrations…" />
      ) : (
        <IntegrationOverview integrations={integrations} onAction={onAction} busyId={busyId} testResults={testResults} />
      )}
    </div>
  );
}
